import React, { useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { State } from 'src/state/ducks';
import * as NotificationActions from 'src/state/ducks/notification/actions';
import { NotificationEntity } from 'src/firebase/db/Notifications';

import StyleConfig from 'src/constants/StyleConfig';
import { DefaultPageProps, HistoryProps } from 'src/constants/Interface';
import { formatDateToDate, copyTimeOnlyHHmmss } from 'src/helpers/UtilityHelper';

import { DefaultLayout } from 'src/components/layouts/DefaultLayout';
import Heading from 'src/components/atoms/typo/Heading';
import Button from 'src/components/atoms/button/Button';
import SpinnerModal from 'src/components/organisms/loading/SpinnerModal';
import NotificationConfirmForm from 'src/components/organisms/form/NotificationConfirmForm';

type OwnProps = {
  notification_template_ids: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const NotificationMultipleConfirmPage = (props: Props) => {
  const {
    notification_template_ids,
    getTemplatesNotifications,
    createNotifications,
    isFetching,
    notifications,
    history,
  } = props;

  // 初期表示：テンプレートIDの配列からデータを取得
  useEffect(() => {
    if (notification_template_ids) {
      getTemplatesNotifications(notification_template_ids.split(','));
    }
  }, [getTemplatesNotifications, notification_template_ids]);

  // 3分前以降のデータだけ抽出（通知が直近すぎるのはよくない）
  const filteredNotifications = useMemo(
    () =>
      notifications.filter((notification) => {
        const time = copyTimeOnlyHHmmss(formatDateToDate(notification.scheduled_time));
        const deadline = dayjs(new Date()).add(1, 'minute').toDate();
        return dayjs(time).isAfter(deadline);
      }),
    [notifications],
  );

  const onSubmit = useCallback(() => {
    createNotifications(notifications);
  }, [createNotifications, notifications]);

  const onBack = useCallback(() => {
    history.push('/notification_templates');
  }, [history]);

  const onBackWithParams = useCallback(() => {
    history.push(`/notification_templates/?notification_template_ids=${notification_template_ids}`);
  }, [history, notification_template_ids]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <div className="p-NotificationMultipleConfirmPage">
          <Heading tag="h1" text={`お知らせ内容のご確認`} />
          {notifications.length && !filteredNotifications.length ? (
            <div className="warnings">
              <p>
                お知らせ時刻が不正なデータが含まれています。
                <br />
                お知らせ時刻が最低でも1分より前に設定されたテンプレートを再選択してください。
              </p>
              <div className="warnings-button">
                <Button text="戻る" buttonColor="white" isDisabled={false} clickEvent={onBack} />
              </div>
            </div>
          ) : (
            <>
              {filteredNotifications.map((notification, i) => (
                <div className="confirm" key={`${i}|notification`}>
                  <NotificationConfirmForm
                    notification={notification}
                    isFetching={isFetching}
                    hideActions
                    forceShowTemplateTitle
                  />
                </div>
              ))}
              <div className="actions">
                <div className="actions-back">
                  <Button
                    text="戻る"
                    buttonColor="white"
                    isDisabled={isFetching}
                    clickEvent={onBackWithParams}
                  />
                </div>
                <div className="actions-next">
                  <Button text="お知らせする" isDisabled={isFetching} clickEvent={onSubmit} />
                </div>
              </div>
            </>
          )}
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  .p-NotificationMultipleConfirmPage {
    .warnings {
      p {
        font-weight: bold;
        margin-bottom: ${StyleConfig.HELPER.M20};
      }
      .warnings-button {
        width: 50%;
        margin: 0 auto;
      }
    }
    > .lead {
      margin-bottom: ${StyleConfig.HELPER.M20};
    }
    > .item {
      margin: ${StyleConfig.HELPER.M24} auto;
    }
    .confirm {
      &:not(:last-child) {
        margin-bottom: ${StyleConfig.HELPER.M32};
        padding-bottom: ${StyleConfig.HELPER.M32};
        border-bottom: 3px solid ${StyleConfig.COLOR.INPUT_BORDER};
      }
    }
    .actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 ${StyleConfig.HELPER.M16};
      margin-top: 52px;
      > * {
        width: 40%;
      }
    }
  }
`;

const mapStateToProps = (state: State) => {
  return {
    shop: state.shop.currentShop.resource,
    notifications: state.notification.templates.resource,
    isFetching: state.notification.templates.isFetching || state.notification.isFetching,
    error: state.notificationTemplate.apiError,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getTemplatesNotifications: (templateIds: string[]) =>
    dispatch(NotificationActions.getTemplatesNotifications(templateIds)),
  createNotifications: (notifications: NotificationEntity[]) =>
    dispatch(NotificationActions.createNotifications(notifications)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationMultipleConfirmPage);
